import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, CheckCircle, XCircle, Loader2, Clock, ChevronDown,
  ChevronUp, Terminal, RefreshCw, Zap, GripVertical,
  RotateCcw, Ban,
} from 'lucide-react';
import {
  listExecutions, getExecutionLogs, createExecutionWS, createWorkflowWS,
  getAgentSpans, cancelExecution, resumeFromNode,
} from '../../lib/workflowApi';
import { useTheme } from '../../contexts/ThemeContext';

const STATUS_COLORS = {
  success: '#10B981',
  failed: '#EF4444',
  running: '#F59E0B',
  pending: '#64748B',
  skipped: '#64748B',
};

const STATUS_ICONS = {
  success: CheckCircle,
  failed: XCircle,
  running: Loader2,
  pending: Clock,
};

function formatMs(ms) {
  if (!ms && ms !== 0) return '';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour12: false });
}

function LogEntry({ log, onFullScreen, isDark, onResume, resumingNodeId }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor = STATUS_COLORS[log.status] || '#94A3B8';
  const StatusIcon = STATUS_ICONS[log.status] || Clock;
  const isResumingThis = resumingNodeId === log.node_id;

  const bg = isDark ? '#0a0f18' : '#ffffff';
  const border = log.status === 'failed'
    ? 'rgba(239,68,68,0.35)'
    : (isDark ? '#1a2333' : '#e2e8f0');
  const text1 = isDark ? '#C9D1D9' : '#0f172a';
  const text2 = isDark ? '#64748B' : '#64748b';
  const jsonBg = isDark ? '#080d14' : '#f8fafc';

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <StatusIcon
            size={12}
            color={statusColor}
            style={log.status === 'running' ? { animation: 'spin 0.9s linear infinite' } : {}}
          />
          <span style={{ fontSize: 12, fontWeight: 600, color: text1 }}>
            {log.node_id?.slice(0, 8)}…
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '1px 6px',
              borderRadius: 100,
              background: `${statusColor}18`,
              color: statusColor,
            }}
          >
            {log.status}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {log.duration_ms > 0 && (
            <span style={{ fontSize: 10, color: text2 }}>
              {formatMs(log.duration_ms)}
            </span>
          )}
          {/* Resume-from-here button — only for failed nodes */}
          {log.status === 'failed' && onResume && (
            <button
              title="Resume workflow from this node"
              onClick={e => { e.stopPropagation(); onResume(log.node_id); }}
              disabled={!!resumingNodeId}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '2px 7px', borderRadius: 6,
                background: isResumingThis ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.1)',
                border: '1px solid rgba(139,92,246,0.4)',
                color: '#A78BFA', fontSize: 10, fontWeight: 700,
                cursor: resumingNodeId ? 'not-allowed' : 'pointer',
                opacity: resumingNodeId && !isResumingThis ? 0.45 : 1,
              }}
            >
              {isResumingThis
                ? <Loader2 size={10} style={{ animation: 'spin 0.9s linear infinite' }} />
                : <RotateCcw size={10} />}
              {isResumingThis ? 'Starting…' : 'Resume here'}
            </button>
          )}
          {expanded ? (
            <ChevronUp size={12} color={text2} />
          ) : (
            <ChevronDown size={12} color={text2} />
          )}
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0 10px 10px', borderTop: `1px solid ${border}` }}>
          {log.error && (
            <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: '5px 8px', margin: '8px 0 6px' }}>
              <span style={{ color: '#EF4444', fontSize: 11 }}>⚠ {log.error}</span>
            </div>
          )}
          {log.output_data && Object.keys(log.output_data).length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: text2, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '6px 0 3px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Output</span>
                <span
                  style={{ cursor: 'pointer', color: '#8B5CF6', textTransform: 'none', fontSize: 10 }}
                  onClick={() => onFullScreen('Output Data', log.output_data)}
                >
                  View Full Screen
                </span>
              </div>
              <pre
                style={{ background: jsonBg, borderRadius: 6, padding: '6px 8px', fontSize: 10, color: '#10B981', overflow: 'auto', maxHeight: 150, margin: 0, fontFamily: 'monospace', lineHeight: 1.5, cursor: 'pointer' }}
                onClick={() => onFullScreen('Output Data', log.output_data)}
                title="Click to view full screen"
              >
                {JSON.stringify(log.output_data, null, 2)}
              </pre>
            </>
          )}
          {log.input_data && Object.keys(log.input_data).length > 0 && (
            <>
              <div style={{ fontSize: 9, fontWeight: 700, color: text2, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '6px 0 3px', display: 'flex', justifyContent: 'space-between' }}>
                <span>Input</span>
                <span
                  style={{ cursor: 'pointer', color: '#8B5CF6', textTransform: 'none', fontSize: 10 }}
                  onClick={() => onFullScreen('Input Data', log.input_data)}
                >
                  View Full Screen
                </span>
              </div>
              <pre
                style={{ background: jsonBg, borderRadius: 6, padding: '6px 8px', fontSize: 10, color: '#10B981', overflow: 'auto', maxHeight: 150, margin: 0, fontFamily: 'monospace', lineHeight: 1.5, cursor: 'pointer' }}
                onClick={() => onFullScreen('Input Data', log.input_data)}
                title="Click to view full screen"
              >
                {JSON.stringify(log.input_data, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ExecutionPanel({ workflowId, currentExecution, onEvent, onClear, onClose, onNewExecution }) {
  const { isDark } = useTheme();
  const [executions, setExecutions] = useState([]);
  const [selectedExec, setSelectedExec] = useState(null);
  const [logs, setLogs] = useState([]);
  const [agentSpans, setAgentSpans] = useState([]);
  const [wsEvents, setWsEvents] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [activeTab, setActiveTab] = useState('live'); // 'live' | 'history' | 'logs' | 'thoughts'
  const [fullScreenData, setFullScreenData] = useState(null);
  const [resumingNodeId, setResumingNodeId] = useState(null);  // node_id being resumed
  const [cancellingExecId, setCancellingExecId] = useState(null); // exec being cancelled
  const [actionToast, setActionToast] = useState(null);          // { msg, ok }
  const wsRef = useRef(null);
  const workflowWsRef = useRef(null);
  // -1 = never loaded; used to skip auto-connect on the very first poll so we
  // don't immediately connect to a completed historical execution on page open.
  const prevExecCountRef = useRef(-1);
  const liveRef = useRef(null);

  const [panelWidth, setPanelWidth] = useState(300);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(300);

  const onMouseDownResize = useCallback((e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizing.current) return;
      const delta = startX.current - e.clientX; // dragging left increases width
      const newWidth = Math.min(600, Math.max(250, startWidth.current + delta));
      setPanelWidth(newWidth);
    };
    const onMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const loadLogs = async (execId) => {
    setLoadingLogs(true);
    setAgentSpans([]);
    try {
      const res = await getExecutionLogs(execId);
      setLogs(res.data);
      // Also load Autonomous Engine spans if any exist
      try {
        const spanRes = await getAgentSpans(execId);
        setAgentSpans(spanRes.data || []);
      } catch { /* not an autonomous run — ignore */ }
    } catch { /* ignore */ } finally {
      setLoadingLogs(false);
    }
  };

  const connectWS = useCallback((execId) => {
    if (wsRef.current) wsRef.current.close();
    setWsEvents([]);
    onClear?.();

    const ws = createExecutionWS(execId);
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      try {
        const event = JSON.parse(ev.data);
        setWsEvents((prev) => [...prev, event]);
        onEvent?.(event);
        if (liveRef.current) liveRef.current.scrollTop = liveRef.current.scrollHeight;
        if (event.type === 'execution_completed' || event.type === 'execution_failed') {
          // Reload history so status column updates
          setTimeout(() => loadHistoryRef.current?.(), 500);
          // Also reload logs for the currently selected execution so the Logs
          // tab is populated immediately after the run finishes.
          setSelectedExec((prev) => {
            if (prev?.id === execId) {
              loadLogs(execId);
            }
            return prev;
          });
        }
      } catch { /* ignore */ }
    };

    ws.onerror = () => {
      setWsEvents((prev) => [...prev, { type: 'ws_error', message: 'WebSocket connection failed' }]);
    };
  }, [onClear, onEvent]);

  const loadHistory = useCallback(async () => {
    if (!workflowId || workflowId === 'new') return;
    try {
      const res = await listExecutions(workflowId);
      const incoming = res.data;
      setExecutions(incoming);

      // First load: record the baseline count and stop — do NOT auto-connect
      // to existing completed executions (that would trigger a WS error because
      // no events will arrive for an already-finished run).
      if (prevExecCountRef.current === -1) {
        prevExecCountRef.current = incoming.length;
        return;
      }

      // Subsequent polls: auto-connect only when a *new* non-manual execution
      // appears (webhook / schedule trigger while the user has the editor open).
      if (incoming.length > prevExecCountRef.current) {
        const newExec = incoming[0];
        if (newExec && newExec.trigger_type !== 'manual') {
          setSelectedExec(newExec);
          setActiveTab('live');
          connectWS(newExec.id);
        }
      }
      prevExecCountRef.current = incoming.length;
    } catch { /* ignore */ }
  }, [workflowId, connectWS]);

  // Ref to break circular dep: connectWS → loadHistory → connectWS
  const loadHistoryRef = useRef(loadHistory);
  useEffect(() => { loadHistoryRef.current = loadHistory; }, [loadHistory]);

  useEffect(() => {
    loadHistory();
    // Poll every 5s as a fallback — the workflow-level WS below handles
    // real-time detection so this is just a safety net.
    const interval = setInterval(loadHistory, 5_000);
    return () => clearInterval(interval);
  }, [loadHistory]);

  // Subscribe to the workflow-level WebSocket to receive execution_started
  // events in real-time for webhook / schedule triggers.  When the event
  // arrives we immediately connect to the per-execution channel — no polling
  // delay, no missed events.
  useEffect(() => {
    if (!workflowId || workflowId === 'new') return;
    const ws = createWorkflowWS(workflowId);
    workflowWsRef.current = ws;

    ws.onmessage = (ev) => {
      try {
        const event = JSON.parse(ev.data);
        // execution_started is broadcast to the workflow channel by the engine.
        // Ignore replayed events (those come from the execution channel).
        if (
          event.type === 'execution_started' &&
          event.execution_id &&
          !event.replayed
        ) {
          setActiveTab('live');
          connectWS(event.execution_id);
          // Refresh history list so the new run appears in the History tab.
          loadHistoryRef.current?.();
        }
      } catch { /* ignore */ }
    };

    return () => {
      ws.close();
      workflowWsRef.current = null;
    };
  }, [workflowId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentExecution?.workflow_id) return;

    // If the API returned a pre-created execution_id, connect the WebSocket
    // immediately — no polling needed, no race condition.
    if (currentExecution.execution_id) {
      const execId = currentExecution.execution_id;
      // Optimistically add to history list so it appears without waiting for poll
      setExecutions((prev) => {
        const alreadyPresent = prev.some((e) => e.id === execId);
        if (alreadyPresent) return prev;
        return [{ id: execId, status: 'pending', trigger_type: 'manual', started_at: new Date().toISOString() }, ...prev];
      });
      setSelectedExec({ id: execId, status: 'pending', trigger_type: 'manual', started_at: new Date().toISOString() });
      setActiveTab('live');
      connectWS(execId);
      return;
    }

    // Fallback for paths that don't return execution_id (e.g. webhook/schedule)
    const checkForExecution = async () => {
      try {
        const res = await listExecutions(workflowId);
        const execs = res.data;
        setExecutions(execs);
        if (execs.length > 0) {
          const latest = execs[0];
          setSelectedExec(latest);
          connectWS(latest.id);
        }
      } catch { /* ignore */ }
    };
    setTimeout(checkForExecution, 400);
  }, [currentExecution]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectExec = (exec) => {
    setSelectedExec(exec);
    setActiveTab('logs');
    loadLogs(exec.id);
  };

  // ── Resume from a specific failed node ───────────────────────────────
  const handleResumeFromNode = useCallback(async (nodeId) => {
    if (!selectedExec) return;
    setResumingNodeId(nodeId);
    try {
      const res = await resumeFromNode(selectedExec.id, nodeId);
      const newExecId = res.data.execution_id;
      // Notify parent (WorkflowEditor) so it connects the WS immediately
      onNewExecution?.({ execution_id: newExecId, workflow_id: workflowId, status: 'pending', trigger_type: 'resume' });
      // Optimistically add to history list
      setExecutions(prev => {
        if (prev.some(e => e.id === newExecId)) return prev;
        return [{ id: newExecId, status: 'pending', trigger_type: 'resume', started_at: new Date().toISOString() }, ...prev];
      });
      setActiveTab('live');
      connectWS(newExecId);
      setActionToast({ msg: 'Resuming from node…', ok: true });
    } catch (ex) {
      setActionToast({ msg: ex?.response?.data?.detail || 'Failed to resume', ok: false });
    } finally {
      setResumingNodeId(null);
      setTimeout(() => setActionToast(null), 3500);
    }
  }, [selectedExec, workflowId, onNewExecution, connectWS]);

  // ── Cancel a running execution from the History tab ──────────────────
  const handleCancelExecution = useCallback(async (exec) => {
    setCancellingExecId(exec.id);
    try {
      await cancelExecution(workflowId, exec.id);
      setExecutions(prev => prev.map(e => e.id === exec.id ? { ...e, status: 'cancelled' } : e));
      setActionToast({ msg: 'Execution cancelled', ok: true });
    } catch (ex) {
      setActionToast({ msg: ex?.response?.data?.detail || 'Cancel failed', ok: false });
    } finally {
      setCancellingExecId(null);
      setTimeout(() => setActionToast(null), 3500);
    }
  }, [workflowId]);



  const panelBg = isDark ? '#0d1117' : '#f8fafc';
  const panelBorder = isDark ? '#1F2937' : '#e2e8f0';
  const text1 = isDark ? '#E6EDF3' : '#0f172a';
  const text2 = isDark ? '#64748B' : '#64748b';
  const eventLineBg = isDark ? '#0a0f18' : '#ffffff';

  const renderEventLine = (event, idx) => {
    const typeColors = {
      execution_started: '#94A3B8',
      node_started: '#F59E0B',
      node_completed: '#10B981',
      node_failed: '#EF4444',
      node_skipped: '#64748B',
      execution_completed: '#10B981',
      execution_failed: '#EF4444',
    };
    const color = typeColors[event.type] || '#94A3B8';

    return (
      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 8px', background: eventLineBg, borderRadius: 6, flexWrap: 'wrap', border: `1px solid ${isDark ? 'transparent' : '#e2e8f0'}` }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ color, fontSize: 11, fontWeight: 600 }}>{event.type.replace(/_/g, ' ')}</span>
        {event.node_id && <span style={{ color: text2, fontSize: 10 }}>→ {event.node_id?.slice(0, 8)}</span>}
        {event.error && <span style={{ color: '#EF4444', fontSize: 10 }}>: {event.error}</span>}
        {event.duration_ms && <span style={{ color: text2, fontSize: 10 }}>({formatMs(event.duration_ms)})</span>}
      </div>
    );
  };

  return (
    <div style={{
      width: panelWidth,
      flexShrink: 0,
      background: panelBg,
      borderLeft: `1px solid ${panelBorder}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
      position: 'relative',
    }}>
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'ew-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          background: 'transparent',
          borderLeft: '2px solid transparent',
        }}
        onMouseDown={onMouseDownResize}
        title="Drag to resize"
      >
        <GripVertical size={12} color={isDark ? '#374151' : '#cbd5e1'} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderBottom: `1px solid ${panelBorder}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Terminal size={14} color="#8B5CF6" />
          <span style={{ fontSize: 13, fontWeight: 700, color: text1 }}>Execution Monitor</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: text2, padding: 4, display: 'flex', alignItems: 'center' }} onClick={loadHistory} title="Refresh history">
            <RefreshCw size={13} />
          </button>
          <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: text2, padding: 4, display: 'flex', alignItems: 'center' }} onClick={onClose}>
            <X size={13} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${panelBorder}`, flexShrink: 0 }}>
        {['live', 'history', 'logs', 'thoughts'].map((tab) => (
          <button
            key={tab}
            style={{
              flex: 1, padding: '8px 0', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'color 0.15s',
              borderBottom: activeTab === tab ? '2px solid #8B5CF6' : '2px solid transparent',
              color: activeTab === tab ? '#8B5CF6' : text2,
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'thoughts' ? '🧠 Thoughts' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'live' && wsEvents.length > 0 && (
              <span style={{ fontSize: 9, background: '#8B5CF6', color: '#fff', padding: '1px 5px', borderRadius: 100, fontWeight: 700 }}>{wsEvents.length}</span>
            )}
            {tab === 'thoughts' && agentSpans.length > 0 && (
              <span style={{ fontSize: 9, background: '#F97316', color: '#fff', padding: '1px 5px', borderRadius: 100, fontWeight: 700 }}>{agentSpans.length}</span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'live' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: 10, display: 'flex', flexDirection: 'column', gap: 4 }} ref={liveRef}>
          {wsEvents.length === 0 ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <Zap size={22} color={isDark ? '#374151' : '#cbd5e1'} />
              <p style={{ color: text2, fontSize: 12, marginTop: 8, margin: '8px 0 0' }}>Run a workflow to see real-time events</p>
            </div>
          ) : (
            wsEvents.map(renderEventLine)
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {executions.length === 0 && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <Clock size={22} color={isDark ? '#374151' : '#cbd5e1'} />
              <p style={{ color: text2, fontSize: 12, marginTop: 8, margin: '8px 0 0' }}>No executions yet</p>
            </div>
          )}
          {executions.map((ex) => {
            const statusColor = STATUS_COLORS[ex.status] || text2;
            const StatusIcon = STATUS_ICONS[ex.status] || Clock;
            const isRunning = ex.status === 'running';
            const isCancelling = cancellingExecId === ex.id;
            return (
              <div
                key={ex.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', background: eventLineBg, borderRadius: 8, cursor: 'pointer', transition: 'border-color 0.15s',
                  border: selectedExec?.id === ex.id ? '1px solid rgba(139,92,246,0.6)' : `1px solid ${isDark ? '#1F2937' : '#e2e8f0'}`,
                }}
                onClick={() => handleSelectExec(ex)}
              >
                <StatusIcon size={12} color={statusColor} style={isRunning ? { animation: 'spin 0.9s linear infinite' } : {}} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: isDark ? '#94A3B8' : '#334155', fontWeight: 600 }}>{ex.id.slice(0, 12)}…</div>
                  <div style={{ fontSize: 10, color: text2 }}>{formatTime(ex.started_at)} · {ex.trigger_type}</div>
                </div>
                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 100, background: `${statusColor}15`, color: statusColor, flexShrink: 0 }}>{ex.status}</span>
                {/* Cancel button for running executions */}
                {isRunning && (
                  <button
                    title="Cancel this execution"
                    onClick={e => { e.stopPropagation(); handleCancelExecution(ex); }}
                    disabled={!!isCancelling}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 3,
                      padding: '2px 6px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.4)',
                      background: 'rgba(239,68,68,0.1)', color: '#EF4444',
                      fontSize: 10, fontWeight: 700, cursor: isCancelling ? 'not-allowed' : 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    {isCancelling ? <Loader2 size={9} style={{ animation: 'spin 0.9s linear infinite' }} /> : <Ban size={9} />}
                    {isCancelling ? '…' : 'Cancel'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'logs' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {!selectedExec && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <p style={{ color: text2, fontSize: 12 }}>Select an execution from History to view logs</p>
            </div>
          )}
          {loadingLogs && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <Loader2 size={22} color="#8B5CF6" style={{ animation: 'spin 0.9s linear infinite' }} />
            </div>
          )}
          {/* Banner for failed executions */}
          {!loadingLogs && selectedExec && selectedExec.status === 'failed' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, marginBottom: 2 }}>
              <XCircle size={12} color="#EF4444" />
              <span style={{ fontSize: 11, color: '#EF4444', fontWeight: 600 }}>Execution failed — click "Resume here" on any failed node to restart from that point.</span>
            </div>
          )}
          {!loadingLogs && selectedExec && logs.map((log) => (
            <LogEntry
              key={log.id}
              log={log}
              onFullScreen={(title, data) => setFullScreenData({ title, data })}
              isDark={isDark}
              onResume={selectedExec.status === 'failed' ? handleResumeFromNode : null}
              resumingNodeId={resumingNodeId}
            />
          ))}
          {!loadingLogs && selectedExec && logs.length === 0 && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <p style={{ color: text2, fontSize: 12 }}>No logs for this execution</p>
            </div>
          )}
        </div>
      )}

      {/* ── Thought Chain (Autonomous Engine Inspect) ── */}
      {activeTab === 'thoughts' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {!selectedExec && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <p style={{ color: text2, fontSize: 12 }}>Select an execution from History first</p>
            </div>
          )}
          {selectedExec && agentSpans.length === 0 && !loadingLogs && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <p style={{ color: text2, fontSize: 12 }}>No Autonomous Agent steps for this execution</p>
            </div>
          )}
          {agentSpans.map((span, idx) => {
            const STEP_COLORS = {
              reasoning:  '#8B5CF6',
              tool_call:  '#F97316',
              observation: '#3B82F6',
              final:      '#10B981',
            };
            const STEP_ICONS = {
              reasoning:  '💭',
              tool_call:  '🔧',
              observation: '👁',
              final:      '✅',
            };
            const color = STEP_COLORS[span.step_type] || '#94A3B8';
            const icon  = STEP_ICONS[span.step_type]  || '•';
            const toolName = span.tool_meta?.tool_name;

            return (
              <div
                key={span.id || idx}
                style={{
                  background: isDark ? '#0a0f18' : '#ffffff',
                  border: `1px solid ${color}30`,
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: 11,
                }}
              >
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 13 }}>{icon}</span>
                  <span style={{ fontWeight: 700, color, textTransform: 'capitalize' }}>
                    Step {span.step_number} — {span.step_type.replace('_', ' ')}
                  </span>
                  {toolName && (
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#F97316', fontWeight: 600, background: 'rgba(249,115,22,0.10)', padding: '1px 6px', borderRadius: 100, border: '1px solid rgba(249,115,22,0.25)' }}>
                      🔧 {toolName}
                    </span>
                  )}
                  {span.latency_ms && (
                    <span style={{ fontSize: 9, color: text2 }}>{span.latency_ms}ms</span>
                  )}
                </div>

                {/* Content */}
                {span.content && (
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: isDark ? '#C9D1D9' : '#374151', lineHeight: 1.55, marginTop: 4 }}>
                    {span.content.length > 800 ? span.content.slice(0, 800) + '…' : span.content}
                  </div>
                )}

                {/* Token meta */}
                {(span.tokens_in || span.tokens_out) && (
                  <div style={{ marginTop: 5, fontSize: 9, color: text2, display: 'flex', gap: 8 }}>
                    {span.tokens_in  && <span>↑ {span.tokens_in}  in</span>}
                    {span.tokens_out && <span>↓ {span.tokens_out} out</span>}
                    {span.cost_usd   && <span>💰 ${span.cost_usd.toFixed(4)}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Full Screen Modal */}
      {fullScreenData && (
        <div style={{ position: 'fixed', inset: 0, background: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }} onClick={() => setFullScreenData(null)}>
          <div style={{ background: panelBg, border: `1px solid ${panelBorder}`, borderRadius: 12, width: '100%', maxWidth: 900, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: isDark ? '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)' : '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${panelBorder}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Terminal size={16} color="#8B5CF6" />
                <span style={{ color: text1, fontSize: 14, fontWeight: 600 }}>{fullScreenData.title}</span>
              </div>
              <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: text2, padding: 4, display: 'flex', alignItems: 'center' }} onClick={() => setFullScreenData(null)}>
                <X size={16} />
              </button>
            </div>
            <div style={{ padding: 20, overflowY: 'auto', background: isDark ? '#080d14' : '#ffffff', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flex: 1 }}>
              <pre style={{ background: 'transparent', padding: '6px 8px', fontSize: 12, color: '#10B981', overflowX: 'hidden', overflowY: 'auto', margin: 0, fontFamily: 'monospace', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(fullScreenData.data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
      {/* Action toast (resume / cancel feedback) */}
      {actionToast && (
        <div style={{
          position: 'absolute', bottom: 12, left: 12, right: 12, zIndex: 9998,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 12px', borderRadius: 8,
          background: actionToast.ok ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${actionToast.ok ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)'}`,
          backdropFilter: 'blur(8px)',
        }}>
          {actionToast.ok
            ? <CheckCircle size={12} color="#10B981" />
            : <XCircle size={12} color="#EF4444" />}
          <span style={{ fontSize: 11, fontWeight: 600, color: actionToast.ok ? '#10B981' : '#EF4444' }}>
            {actionToast.msg}
          </span>
        </div>
      )}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
